if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        if (this.dataset.gsnSizeRequired === 'true') {
          customElements.whenDefined('product-form').then(() => {
            if (this.isConnected) this.initGSNSizeChoice();
          });
        }
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      initGSNSizeChoice() {
        if (this.dataset.gsnSizeRequired !== 'true') return;
        const variant = this.getSelectedVariant(this);
        const url = new URL(window.location.href);
        this.gsnSizeChosen = Boolean(variant && String(variant.id) === url.searchParams.get('variant'));
        this.gsnPending = false;
        this.gsnFailed = false;
        this.dataset.gsnDisplayVariant = variant?.id || '';
        if (!this.gsnSizeChosen) this.clearGSNSizeSelection(this.variantSelectors);
        this.syncGSNSizeState(variant, this.querySelector('[name="add"]')?.disabled);
        this.updateVariantInputs(this.gsnSizeChosen ? variant?.id : null);
        this.gsnPurchaseGuard = (event) => {
          if (this.gsnReady) return;
          const purchase = event.type === 'submit'
            ? event.target.matches('form[data-type="add-to-cart-form"]')
            : event.target.closest('button[name="add"], [data-gsn-dynamic-checkout], gsn-bundle [data-add]');
          if (!purchase) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.productForm?.handleErrorMessage(this.gsnFailed ? this.dataset.gsnSelectionError : this.dataset.gsnSizePrompt);
          this.variantSelectors?.querySelector('[data-gsn-size-option] input, [data-gsn-size-option] select')?.focus();
        };
        this.addEventListener('submit', this.gsnPurchaseGuard, true);
        this.addEventListener('click', this.gsnPurchaseGuard, true);

        if (this.gsnSizeChosen || !url.searchParams.has('gsn_color')) return;
        const color = url.searchParams.get('gsn_color').trim().toLowerCase();
        const option = Array.from(this.variantSelectors?.querySelectorAll('[data-gsn-color-option] [data-option-value-id]') || [])
          .find((input) => input.value.trim().toLowerCase() === color);
        if (!option || option.checked || option.selected) return;
        if (option.tagName === 'OPTION') {
          option.parentElement.value = option.value;
          option.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          option.checked = true;
          option.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      clearGSNSizeSelection(picker) {
        const group = picker?.querySelector('[data-gsn-size-option]');
        group?.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.checked = false;
          input.removeAttribute('checked');
        });
        group?.querySelectorAll('option').forEach((option) => {
          option.selected = option.hasAttribute('data-gsn-size-placeholder');
          option.toggleAttribute('selected', option.selected);
        });
        const label = group?.querySelector('[data-selected-value]');
        if (label) label.textContent = this.dataset.gsnSizePrompt;
      }

      syncGSNSizeState(variant, unavailable = false) {
        if (this.dataset.gsnSizeRequired !== 'true') return;
        this.gsnReady = Boolean(this.gsnSizeChosen && !this.gsnPending && !this.gsnFailed && variant?.available && !unavailable);
        this.dataset.gsnSizeState = this.gsnPending ? 'pending' : this.gsnFailed ? 'error'
          : !this.gsnSizeChosen ? 'required' : this.gsnReady ? 'ready' : 'unavailable';
        const text = this.gsnFailed ? this.dataset.gsnSelectionError
          : !this.gsnSizeChosen || this.gsnPending ? this.dataset.gsnSizePrompt
          : variant ? window.variantStrings.soldOut : window.variantStrings.unavailable;
        this.productForm?.toggleSubmitButton(!this.gsnReady, text);
        this.querySelectorAll('[data-gsn-dynamic-checkout]').forEach((checkout) => {
          checkout.hidden = !this.gsnReady;
          checkout.inert = !this.gsnReady;
        });
      }

      beginGSNSelection() {
        if (this.dataset.gsnSizeRequired !== 'true') return;
        this.gsnPending = true;
        this.gsnFailed = false;
        this.dataset.gsnDisplayVariant = '';
        this.syncGSNSizeState(null);
        this.updateVariantInputs(null);
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        this.removeEventListener('submit', this.gsnPurchaseGuard, true);
        this.removeEventListener('click', this.gsnPurchaseGuard, true);
        this.abortController?.abort();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        if (this.dataset.gsnSizeRequired === 'true') {
          if (target.closest('[data-gsn-size-option]') && target.dataset.optionValueId) this.gsnSizeChosen = true;
          this.beginGSNSelection();
        }

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const requestController = this.abortController;

        fetch(requestUrl, { signal: requestController.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`Product selection failed: ${response.status}`);
            return response.text();
          })
          .then((responseText) => {
            if (requestController.signal.aborted) return;
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            if (this.dataset.gsnSizeRequired === 'true' && !html.querySelector('variant-selects [data-selected-variant]')) {
              throw new Error('Product selection response is incomplete');
            }
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError' || requestController.signal.aborted) {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
              if (this.dataset.gsnSizeRequired === 'true') {
                this.pendingRequestUrl = null;
                this.gsnPending = false;
                this.gsnFailed = true;
                this.gsnSizeChosen = false;
                this.dataset.gsnDisplayVariant = '';
                this.clearGSNSizeSelection(this.variantSelectors);
                this.syncGSNSizeState(null);
                this.updateVariantInputs(null);
                this.updateURL(this.dataset.url, null);
                this.productForm?.handleErrorMessage(this.dataset.gsnSelectionError);
              }
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        if (this.dataset.gsnSizeRequired === 'true') {
          optionValues = Array.from(this.variantSelectors.querySelectorAll('.product-form__input')).map((group) => {
            const selected = group.querySelector('input:checked, select option:checked[data-option-value-id]');
            const fallback = group.querySelector('[data-gsn-size-fallback]') || group.querySelector('[data-option-value-id]');
            return (selected || fallback)?.dataset.optionValueId;
          }).filter(Boolean);
        }

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          if (this.dataset.gsnSizeRequired === 'true' && !this.gsnSizeChosen) this.clearGSNSizeSelection(variantSelects);
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);
          if (this.dataset.gsnSizeRequired === 'true') {
            this.gsnPending = false;
            this.gsnFailed = false;
            this.dataset.gsnDisplayVariant = variant?.id || '';
          }

          this.pickupAvailability?.update(this.dataset.gsnSizeRequired === 'true' && !this.gsnSizeChosen ? null : variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            this.syncGSNSizeState(null);
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );
          this.syncGSNSizeState(variant, html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant: this.dataset.gsnSizeRequired === 'true' && !this.gsnSizeChosen ? null : variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        if (this.dataset.gsnSizeRequired === 'true' && (!this.gsnSizeChosen || this.gsnPending || this.gsnFailed)) variantId = null;
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input) return;
          input.value = variantId ?? '';
          if (this.dataset.gsnSizeRequired === 'true') input.disabled = !variantId;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        if (this.dataset.gsnSizeRequired === 'true' && !this.gsnSizeChosen) {
          const color = this.variantSelectors?.querySelector('[data-gsn-color-option] input:checked, [data-gsn-color-option] select option:checked')?.value;
          const displayUrl = `${url}${color ? `?gsn_color=${encodeURIComponent(color)}` : ''}`;
          this.querySelector('share-button')?.updateUrl(`${window.shopUrl}${displayUrl}`);
          if (this.dataset.updateUrl !== 'false') window.history.replaceState({}, '', displayUrl);
          return;
        }
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
